import { Direction } from '../sim/direction';
import type { ScreenPoint } from '../sim/projection';

/**
 * Pixel radius around the player's screen centre inside which the cursor only
 * *turns* the player in place (the spin-in-place zone), never walks. ClassicUO
 * has no such zone - it walks the instant you right-click - but issue #59 wants
 * a small dead radius so circling the cursor near your feet pivots you through
 * all 8 facings without stepping. ~0.75 of a tile (a tile step is 2*TILE_HALF =
 * 44px on screen). Captain-tunable.
 */
export const TURN_RADIUS_PX = 33;

/**
 * Pixel radius beyond which the cursor walk becomes a *run*. ClassicUO's exact
 * constant (`mouseRange >= 190` from the viewport centre in
 * `GameSceneInputHandler.MoveCharacterByMouseInput`); UO tile art and ours share
 * the same 44px tile, so this lands ~4.3 tiles out, matching its feel.
 * Captain-tunable.
 */
export const RUN_RADIUS_PX = 190;

/**
 * The heading the cursor asks for: an 8-way `dir` (or `null` to stop), whether
 * it is a *turn-only* facing (spin in place, never step), and whether the walk
 * is a run. `main.ts` feeds this straight into the `MoveIntent` seam.
 */
export interface CursorIntent {
  dir: Direction | null;
  turnOnly: boolean;
  running: boolean;
}

const STOP: CursorIntent = { dir: null, turnOnly: false, running: false };

/**
 * Resolve a screen-space offset (cursor minus the player's viewport centre) to
 * an 8-way compass facing, faithfully to ClassicUO's `GameCursor.GetMouseDirection`:
 * the sign of each axis picks the quadrant, then the magnitude ratio splits each
 * quadrant into a cardinal vs. a diagonal band. ClassicUO's bands are the 2:5
 * ratios `|dy|*5 <= |dx|*2` (near-horizontal cardinal) and `|dy|*2 >= |dx|*5`
 * (near-vertical cardinal), everything else diagonal - so each octant spans ~45°
 * (cardinals ±21.8°). Screen Y grows downward, and our projection is screen-
 * consistent (N is straight up, E straight right, the diagonals along the screen
 * diagonals - see {@link tileToScreen}), so the screen octant *is* the compass
 * facing. Returns `null` only for the zero offset (dead-centre).
 */
function octantFromScreenOffset(dx: number, dy: number): Direction | null {
  if (dx === 0 && dy === 0) return null;
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  // Near-horizontal: shallow slope -> due E/W. (Also the dy === 0 axis case.)
  if (ay * 5 <= ax * 2) return dx > 0 ? Direction.E : Direction.W;
  // Near-vertical: steep slope -> due N/S. (Also the dx === 0 axis case.)
  if (ay * 2 >= ax * 5) return dy > 0 ? Direction.S : Direction.N;
  // Diagonal quadrant.
  if (dx > 0) return dy > 0 ? Direction.SE : Direction.NE;
  return dy > 0 ? Direction.SW : Direction.NW;
}

/**
 * Pure mapping from "where the player stands on screen" + "where the cursor is"
 * to a {@link CursorIntent}, for classic-UO right-click / right-click-hold
 * movement, modelled on the ClassicUO client (`GameSceneInputHandler` +
 * `GameCursor.GetMouseDirection`). Kept free of any DOM types so it unit-tests in
 * node and stays reusable; `main.ts` owns the actual mouse listeners and feeds in
 * the player's viewport centre ({@link GameRenderer.playerCenter}) and the
 * cursor's canvas point.
 *
 * The cursor's offset from the player centre drives both an **angle** and a
 * **distance**:
 *
 * - **angle** -> the 8-way facing via {@link octantFromScreenOffset} (ClassicUO's
 *   sign + 2:5-ratio bands), screen-consistent with the keyboard.
 * - **distance** -> the gait: inside {@link TURN_RADIUS_PX} the player only turns
 *   in place (`turnOnly: true`) so circling the cursor near your feet spins you
 *   on the spot; between the turn radius and {@link RUN_RADIUS_PX} it walks;
 *   beyond {@link RUN_RADIUS_PX} it runs.
 *
 * A turn-only intent can never move, so holding the cursor at rest near the
 * player can never oscillate the character (the PR #56 anti-oscillation
 * guarantee, preserved by construction - see ADR-0022). Returns a clean stop
 * (`{ null, false, false }`) when the cursor is dead-centre on the player or the
 * player's screen position is not yet known, so the caller routes a stop rather
 * than a phantom step.
 */
export function directionFromCursor(
  center: ScreenPoint | undefined,
  cursor: ScreenPoint,
): CursorIntent {
  if (!center) return STOP;
  const dx = cursor.x - center.x;
  const dy = cursor.y - center.y;
  const dir = octantFromScreenOffset(dx, dy);
  if (dir === null) return STOP; // dead-centre: hold position
  const range = Math.hypot(dx, dy);
  if (range < TURN_RADIUS_PX) return { dir, turnOnly: true, running: false };
  return { dir, turnOnly: false, running: range >= RUN_RADIUS_PX };
}

/**
 * Resolve the gauntlet direction from pointer hover alone.
 * This deliberately accepts no button, keyboard, route, or movement state, so
 * the cursor previews what a right-click would do without reacting to movement
 * that originated elsewhere.
 */
export function previewDirectionFromCursor(
  center: ScreenPoint | undefined,
  cursor: ScreenPoint | undefined,
): Direction | null {
  if (!cursor) return null;
  const intent = directionFromCursor(center, cursor);
  return intent.turnOnly ? null : intent.dir;
}
